import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { FileText, Upload, Trash2, Search, Lock, FolderOpen, Eye, Download, File } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

export function DataRoomPage() {
  const { getAuthHeaders, user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterDeal, setFilterDeal] = useState('');
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  const [uploadForm, setUploadForm] = useState({
    deal_id: '',
    category: 'financial',
    confidentiality: 'restricted'
  });
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [docsRes, dealsRes] = await Promise.all([
        axios.get(`${API}/documents`, { headers: getAuthHeaders() }),
        axios.get(`${API}/deals`, { headers: getAuthHeaders() })
      ]);
      setDocuments(docsRes.data);
      setDeals(dealsRes.data);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 50 * 1024 * 1024) {
        toast.error('File size must be under 50MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const uploadDocument = async () => {
    if (!selectedFile || !uploadForm.deal_id) {
      toast.error('Please select a file and deal');
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('deal_id', uploadForm.deal_id);
      formData.append('category', uploadForm.category);
      formData.append('confidentiality', uploadForm.confidentiality);

      await axios.post(`${API}/documents/upload`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          const pct = Math.round((e.loaded * 100) / e.total);
          setUploadProgress(pct);
        }
      });
      toast.success('Document uploaded successfully');
      setDialogOpen(false);
      setSelectedFile(null);
      setUploadForm({ deal_id: '', category: 'financial', confidentiality: 'restricted' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const deleteDocument = async (id) => {
    try {
      await axios.delete(`${API}/documents/${id}`, { headers: getAuthHeaders() });
      toast.success('Document removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const filteredDocs = documents.filter(d => {
    if (filterDeal && filterDeal !== 'all' && d.deal_id !== filterDeal) return false;
    if (search && !d.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const getConfBadge = (level) => {
    const map = { public: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300', restricted: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300', confidential: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' };
    return map[level] || map.restricted;
  };

  const getFileIcon = (type) => {
    const icons = { pdf: 'text-red-500', xlsx: 'text-green-600', docx: 'text-blue-600', pptx: 'text-orange-500' };
    return icons[type] || 'text-muted-foreground';
  };

  if (loading) return <div className="flex items-center justify-center h-full"><div className="text-muted-foreground">Loading data room...</div></div>;

  return (
    <div className="p-6 space-y-6" data-testid="data-room-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Secure Data Room</h1>
          <p className="text-muted-foreground">Manage and control access to deal documents</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-document-btn"><Upload size={16} className="mr-2" />Upload Document</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Upload Document</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div
                className="border-2 border-dashed border-border rounded-lg p-8 text-center cursor-pointer hover:border-foreground/30 transition-colors"
                onClick={() => fileInputRef.current?.click()}
                data-testid="file-dropzone"
              >
                <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileSelect} accept=".pdf,.docx,.xlsx,.pptx,.csv,.txt,.zip,.png,.jpg" />
                {selectedFile ? (
                  <div className="flex items-center justify-center gap-3">
                    <File size={24} className="text-muted-foreground" />
                    <div className="text-left">
                      <p className="text-sm font-medium">{selectedFile.name}</p>
                      <p className="text-xs text-muted-foreground">{formatFileSize(selectedFile.size)}</p>
                    </div>
                  </div>
                ) : (
                  <div>
                    <Upload size={32} className="mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">Click to select a file (max 50MB)</p>
                    <p className="text-xs text-muted-foreground mt-1">PDF, DOCX, XLSX, PPTX, CSV, TXT, ZIP, PNG, JPG</p>
                  </div>
                )}
              </div>

              <div>
                <Label>Deal</Label>
                <Select value={uploadForm.deal_id} onValueChange={(v) => setUploadForm({...uploadForm, deal_id: v})}>
                  <SelectTrigger data-testid="upload-deal-select"><SelectValue placeholder="Select deal" /></SelectTrigger>
                  <SelectContent>{deals.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Category</Label>
                  <Select value={uploadForm.category} onValueChange={(v) => setUploadForm({...uploadForm, category: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="financial">Financial</SelectItem>
                      <SelectItem value="legal">Legal</SelectItem>
                      <SelectItem value="technical">Technical</SelectItem>
                      <SelectItem value="environmental">Environmental</SelectItem>
                      <SelectItem value="compliance">Compliance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Confidentiality</Label>
                  <Select value={uploadForm.confidentiality} onValueChange={(v) => setUploadForm({...uploadForm, confidentiality: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="public">Public</SelectItem>
                      <SelectItem value="restricted">Restricted</SelectItem>
                      <SelectItem value="confidential">Confidential</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {uploading && <Progress value={uploadProgress} className="h-2" />}

              <Button onClick={uploadDocument} className="w-full" disabled={!selectedFile || !uploadForm.deal_id || uploading} data-testid="submit-upload-btn">
                <Upload size={16} className="mr-2" />{uploading ? `Uploading ${uploadProgress}%...` : 'Upload Document'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search documents..." className="pl-10" data-testid="doc-search" />
            </div>
            <Select value={filterDeal} onValueChange={setFilterDeal}>
              <SelectTrigger className="w-64" data-testid="deal-filter"><SelectValue placeholder="All Deals" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Deals</SelectItem>
                {deals.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">Total Documents</p><p className="text-2xl font-bold font-mono">{documents.length}</p></div><FileText className="h-8 w-8 text-muted-foreground" /></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">Total Size</p><p className="text-2xl font-bold font-mono">{formatFileSize(documents.reduce((s, d) => s + (d.file_size || 0), 0))}</p></div><FolderOpen className="h-8 w-8 text-muted-foreground" /></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">Confidential</p><p className="text-2xl font-bold font-mono">{documents.filter(d => d.confidentiality === 'confidential').length}</p></div><Lock className="h-8 w-8 text-muted-foreground" /></div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">Deals with Docs</p><p className="text-2xl font-bold font-mono">{new Set(documents.map(d => d.deal_id)).size}</p></div><FolderOpen className="h-8 w-8 text-muted-foreground" /></div></CardContent></Card>
      </div>

      {filteredDocs.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">No documents found. Upload your first document to get started.</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {filteredDocs.map(doc => {
            const deal = deals.find(d => d.id === doc.deal_id);
            const hasFile = doc.file_path?.startsWith('/api/documents/download/');
            return (
              <Card key={doc.id} data-testid={`doc-${doc.id}`}>
                <CardContent className="py-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded bg-muted flex items-center justify-center`}>
                        <FileText size={18} className={getFileIcon(doc.file_type)} />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{doc.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {deal?.name || doc.deal_id} &middot; {doc.category} &middot; .{doc.file_type}
                          {doc.file_size ? ` &middot; ${formatFileSize(doc.file_size)}` : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={getConfBadge(doc.confidentiality)}>{doc.confidentiality}</Badge>
                      {hasFile && (user?.role === 'admin' || user?.role === 'internal') && (
                        <a href={`${process.env.REACT_APP_BACKEND_URL}${doc.file_path}`} target="_blank" rel="noopener noreferrer">
                          <Button variant="ghost" size="sm" data-testid={`download-doc-${doc.id}`}><Download size={14} /></Button>
                        </a>
                      )}
                      {(user?.role === 'admin' || user?.role === 'internal') && (
                        <Button variant="ghost" size="sm" onClick={() => deleteDocument(doc.id)} data-testid={`delete-doc-${doc.id}`}><Trash2 size={14} className="text-red-500" /></Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
